import React from 'react';
import * as LucideIcons from 'lucide-react';
import { registerVizType, unregisterVizType, getRegisteredVizType } from '@/lib/customVizRegistry';
import { CustomVisualSandbox } from '@/components/CustomVisualSandbox';
import { api } from '@/lib/api';

/**
 * Custom-visual loader (GAP-7.1)
 * ==============================
 * Fetches the tenant's SDK-authored custom visuals and registers each one
 * as a runtime viz type whose renderer mounts the sandboxed iframe. Safe to
 * call repeatedly — it reconciles (unregisters visuals that were deleted).
 */

const _loaded = new Set(); // viz_key currently registered by this loader

function makeRenderer(jsCode) {
  return function CustomVizRenderer({ data, config, colors, height }) {
    const theme = { colors: colors || [] };
    return (
      <CustomVisualSandbox jsCode={jsCode} data={data} config={config} theme={theme} height={height || 340} />
    );
  };
}

export async function loadTenantCustomVisuals() {
  let items = [];
  try {
    const res = await api.listCustomVisuals();
    items = res?.items || [];
  } catch {
    return; // not logged in / endpoint unavailable — silently skip
  }
  const seen = new Set();
  for (const v of items) {
    if (!v.enabled) continue;
    const key = v.viz_key;
    seen.add(key);
    // Re-register if new or the code changed (unregister first to bypass the
    // "already registered" guard).
    if (_loaded.has(key)) unregisterVizType(key);
    if (getRegisteredVizType(key)) continue; // built-in collision guard
    const Icon = (v.icon && LucideIcons[v.icon]) || LucideIcons.Shapes;
    try {
      registerVizType({
        type: key,
        label: v.name,
        icon: Icon,
        renderer: makeRenderer(v.js_code || ''),
        meta: {
          description: v.description || 'Custom visualization (SDK)',
          defaultConfig: {},
          isCustomSdk: true,
          supportsTopN: false,
          supportsPeriodComparison: false,
          shelves: v.shelves || ['dimensions', 'measures'],
        },
      });
      _loaded.add(key);
    } catch { /* ignore duplicate/registration races */ }
  }
  // Unregister visuals that disappeared.
  for (const key of Array.from(_loaded)) {
    if (!seen.has(key)) { unregisterVizType(key); _loaded.delete(key); }
  }
}
